import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TurnstileWidget, { TurnstileWidgetHandle } from '@/components/TurnstileWidget';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Loader2, ShieldCheck } from 'lucide-react';

const SAFE_NEXT_RE = /^\/[A-Za-z0-9\-_/?&=.%]*$/;

const Challenge = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const widgetRef = useRef<TurnstileWidgetHandle>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rawNext = searchParams.get('next') || '/';
  const nextPath = SAFE_NEXT_RE.test(rawNext) && !rawNext.startsWith('//') ? rawNext : '/';

  const handleToken = async (token: string) => {
    if (!token) return;
    setVerifying(true);
    setError(null);
    try {
      const sessionId = sessionStorage.getItem('visitor_session_id');
      const { data, error: invokeError } = await supabase.functions.invoke('verify-challenge', {
        body: { token, session_id: sessionId },
      });
      if (invokeError || !data?.success) {
        throw new Error(data?.error || 'verification_failed');
      }
      sessionStorage.setItem('challenge_passed', '1');
      navigate(nextPath, { replace: true });
    } catch (e) {
      console.error('challenge verify error:', e);
      setError("La vérification a échoué. Merci de réessayer.");
      widgetRef.current?.reset();
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md p-8 space-y-6 text-center">
        <div className="flex justify-center">
          <div className="rounded-full bg-primary/10 p-4">
            <ShieldCheck className="h-8 w-8 text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold text-foreground">Vérification rapide</h1>
          <p className="text-sm text-muted-foreground">
            Nous vérifions que vous n'êtes pas un robot avant de continuer.
            Cette étape n'est demandée qu'une seule fois.
          </p>
        </div>

        <div className="flex justify-center">
          <TurnstileWidget ref={widgetRef} onToken={handleToken} />
        </div>

        {verifying && (
          <div className="flex items-center justify-center text-sm text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Vérification en cours…
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </Card>
    </div>
  );
};

export default Challenge;
